/**
 * LegalPageShell — thin frame around every /legal/* page that ensures
 * there is always a visible way BACK to the app it was opened from.
 *
 * Behaviour:
 *   • If the page was opened via `window.open` / `target="_blank"` it's
 *     the only history entry — we render a "Close tab" button that calls
 *     `window.close()`.
 *   • Otherwise (deep-linked, refresh, direct hit) we show a "← Back"
 *     link that returns to `document.referrer` if it's on the same
 *     origin, else `/finder` (the phone app landing).
 *
 * Used by:  HustleSuitePrivacyPage, HustleSuiteTermsPage,
 *           HustleSuiteDisclaimerPage, SupportPage.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, X } from "lucide-react";

function _sameOriginReferrer() {
  if (typeof document === "undefined") return null;
  const ref = document.referrer;
  if (!ref) return null;
  try {
    const u = new URL(ref);
    if (u.origin === window.location.origin) return ref;
  } catch (_e) { /* ignore */ }
  return null;
}

export default function LegalPageShell({ children, title }) {
  // Detect whether we can close the tab (only true when the tab was
  // opened by window.open — the browser then blocks history back to
  // the origin, and window.close() is allowed).
  const [canClose, setCanClose] = useState(false);
  const backHref = useMemo(() => _sameOriginReferrer() || "/finder", []);

  useEffect(() => {
    // window.opener is set when opened via <a target="_blank"> without
    // rel="noopener", OR via window.open. To keep security we always use
    // rel="noopener" — so we detect "was opened in a new tab" by the
    // history length instead (length === 1 means no prior page).
    setCanClose(
      typeof window !== "undefined" &&
      window.history.length <= 1
    );
  }, []);

  const _close = () => {
    // window.close() only works on tabs opened via script. Fall back to
    // navigating to /finder if the browser blocks it.
    try { window.close(); } catch (_e) { /* ignore */ }
    setTimeout(() => {
      if (typeof window !== "undefined" && !window.closed) {
        window.location.href = "/finder";
      }
    }, 200);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="legal-shell-root">
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 h-11 flex items-center gap-3">
          {canClose ? (
            <button
              type="button"
              onClick={_close}
              data-testid="legal-shell-close"
              className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-emerald-700"
            >
              <X size={16}/> Close
            </button>
          ) : (
            <a
              href={backHref}
              data-testid="legal-shell-back"
              className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-emerald-700"
            >
              <ArrowLeft size={16}/> Back to app
            </a>
          )}
          <span className="text-sm text-slate-500 truncate" data-testid="legal-shell-title">
            {title}
          </span>
        </div>
      </div>
      {children}
    </div>
  );
}
